import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { 
  knowledgeBaseManager,
  getAllArticles,
  getArticlesByCategory,
  getBookmarkedArticles,
  getRecentlyViewedArticles,
  searchArticles,
  markArticleViewed,
  toggleArticleBookmark,
  getCategoriesWithCounts,
  KNOWLEDGE_BASE_CATEGORIES
} from '../../utils/knowledgeBaseData';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';

const KnowledgeBaseApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [articles, setArticles] = useState([]);
  const [activeTab, setActiveTab] = useState('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // Load articles on component mount in background
  useEffect(() => {
    loadArticles();
  }, [activeTab, selectedCategory]);

  const loadArticles = () => {
    try {
      const allArticles = getAllArticles();
      setArticles([...allArticles]);
    } catch (error) {
      console.error('Error loading knowledge base articles:', error);
      dispatch(setErrorAlert('Failed to load knowledge base articles'));
    }
  };

  const handleArticleClick = (articleId) => {
    try {
      markArticleViewed(articleId);
      // Navigate to article detail page (we'll create this next)
      navigate(`/knowledge-base/article/${articleId}`);
    } catch (error) {
      console.error('Error opening article:', error);
      dispatch(setErrorAlert('Failed to open article'));
    }
  };

  const handleBookmarkToggle = (articleId, event) => {
    event.stopPropagation(); // Prevent triggering article click
    try {
      const isBookmarked = toggleArticleBookmark(articleId);
      // Force re-render
      loadArticles();
      dispatch(setSuccessAlert(isBookmarked ? 'Article bookmarked!' : 'Bookmark removed'));
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      dispatch(setErrorAlert('Failed to update bookmark'));
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      loadArticles();
    } else {
      try {
        const results = searchArticles(query, { 
          category: selectedCategory || undefined 
        });
        setArticles(results);
      } catch (error) {
        console.error('Error searching articles:', error);
        dispatch(setErrorAlert('Search failed'));
      }
    }
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    setSearchQuery('');
    if (category === '') {
      loadArticles();
    } else {
      const filtered = getArticlesByCategory(category);
      setArticles(filtered);
    }
  };

  // Tab filtering
  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'all', label: 'All' },
    { id: 'bookmarked', label: 'Bookmarked' },
    { id: 'recent', label: 'Recent' },
    { id: 'categories', label: 'Categories' }
  ];

  const filteredArticles = articles.filter(article => {
    let matches = true;

    switch (activeTab) {
      case 'home':
        matches = false; // Home tab doesn't show articles
        break;
      case 'bookmarked':
        matches = article.bookmarked;
        break;
      case 'recent':
        matches = article.viewed;
        break;
      case 'categories':
        matches = selectedCategory ? article.category === selectedCategory : true;
        break;
      default:
        matches = true;
    }

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      matches = matches && (
        article.title.toLowerCase().includes(query) ||
        article.summary.toLowerCase().includes(query) ||
        article.tags.some(tag => tag.toLowerCase().includes(query)) ||
        article.category.toLowerCase().includes(query)
      );
    }

    return matches;
  });

  // Get category color
  const getCategoryColor = (categoryName) => {
    const category = Object.values(KNOWLEDGE_BASE_CATEGORIES).find(cat => cat.name === categoryName);
    return category ? category.color : '#007AFF';
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return '#34C759';
      case 'Intermediate': return '#FF9500';
      case 'Advanced': return '#FF3B30';
      default: return '#007AFF';
    }
  };


  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Knowledge</span>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setShowSearch(!showSearch)}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Knowledge
              </button>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  const canGoBack = window.history.length > 1;
                  if (canGoBack) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Search Bar */}
          {showSearch && (
            <div className="w-full px-4 mb-4">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF1493]"
              />
            </div>
          )}

          {/* Category Filter */}
          {activeTab === 'categories' && (
            <div className="w-full px-4 mb-4">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryFilter(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF1493]"
              >
                <option value="">All Categories</option>
                {Object.values(KNOWLEDGE_BASE_CATEGORIES).map(category => (
                  <option key={category.id} value={category.name} className="bg-black">
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Tabs */}
          <div className="w-full mb-6 px-4">
            <div className="flex overflow-x-auto scrollbar-hide">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FF1493]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {activeTab === 'home' ? (
              <div className="flex flex-col gap-4 mb-8">
                <div className="mb-4 pt-8">
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5] mb-6">
                    Your hub for clear answers, guides, and music-industry best practices.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Browse & Search Anything</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Browse topics and ask about releases, touring, marketing and registrations and filter results by category to find what you need faster.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Step By Step Guides</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Follow simple, detailed walkthroughs for common industry actions, complete with links to the relevant sites and tools you need.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Expert Opinions</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Read insights, perspectives, and real-world advice from experienced artists, managers, and industry professionals.
                  </p>
                </div>

                <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                  <h3 className="text-white text-[12px] font-normal mb-2">Bookmarks</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Save your favorite articles for quick access anytime.
                  </p>
                </div>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-normal mb-2">
                  No articles found
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  {searchQuery 
                    ? `No articles match "${searchQuery}". Try different keywords or browse categories.`
                    : activeTab === 'bookmarked' 
                      ? "You haven't bookmarked any articles yet. Click the bookmark icon on articles you want to save."
                      : activeTab === 'recent'
                        ? "You haven't viewed any articles yet. Browse through the knowledge base to get started."
                        : "No articles available in this category."
                  }
                </p>
                {searchQuery && (
                  <button 
                    onClick={() => {
                      setSearchQuery('');
                      loadArticles();
                    }}
                    className="bg-[#FF1493] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(255,20,147,0.8)] transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredArticles.map((article) => (
                  <div 
                    key={article.id}
                    onClick={() => handleArticleClick(article.id)}
                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF1493] hover:bg-[rgba(255,20,147,0.05)] transition-all cursor-pointer"
                  >
                    <div className="w-full">
                      {/* Article Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-white text-[14px] font-normal break-words mb-1">
                            {article.title}
                          </h3>
                          <div className="flex items-center gap-2 mb-2">
                            <span 
                              className="text-[9px] px-2 py-1 rounded font-medium"
                              style={{ 
                                backgroundColor: `${getCategoryColor(article.category)}20`,
                                color: getCategoryColor(article.category)
                              }}
                            >
                              {article.category}
                            </span>
                            <span 
                              className="text-[9px] px-2 py-1 rounded font-medium"
                              style={{ 
                                backgroundColor: `${getDifficultyColor(article.difficulty)}20`,
                                color: getDifficultyColor(article.difficulty)
                              }}
                            >
                              {article.difficulty}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleBookmarkToggle(article.id, e)}
                          className={`ml-2 p-1 rounded transition-colors ${
                            article.bookmarked 
                              ? 'text-yellow-400 hover:text-yellow-300' 
                              : 'text-[rgba(255,255,255,0.3)] hover:text-yellow-400'
                          }`}
                        >
                          <svg className="w-4 h-4" fill={article.bookmarked ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                          </svg>
                        </button>
                      </div>
                        
                      <p className="text-[rgba(255,255,255,0.6)] text-[11px] mb-3 leading-tight break-words">
                        {article.summary}
                      </p>

                      {/* Article Meta */}
                      <div className="flex items-center gap-3 text-[10px] text-[rgba(255,255,255,0.4)]">
                        <span>{article.getReadingTime()} min read</span>
                        <span>{article.timeToComplete}</span>
                      </div>

                      {/* Tags */}
                      {article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {article.tags.slice(0, 3).map(tag => (
                            <span 
                              key={tag}
                              className="text-[8px] px-2 py-1 rounded bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.6)]"
                            >
                              #{tag}
                            </span>
                          ))}
                          {article.tags.length > 3 && (
                            <span className="text-[8px] px-2 py-1 rounded bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.6)]">
                              +{article.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default KnowledgeBaseApp; 